import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check } from 'lucide-react';
import { cn } from 'lib/utils';
import { getQuestionOptionLabel } from 'lib/questionOptions';
import { RenderedHtml, htmlIsEmpty } from 'modules/editor';
import type {
  BlankSlot,
  ReorderItem,
  PassagePage,
} from '@examing/types';
import { FillInBlanksPlayer } from './FillInBlanksPlayer';
import { OpenEndedPlayer } from './OpenEndedPlayer';
import { ReorderPlayer } from './ReorderPlayer';
import { PassagePlayer } from './PassagePlayer';
import {
  correctBlanksValue,
  correctPassageValue,
  correctReorderValue,
} from './correctValue';

/**
 * The canonical read-only rendering of a question with its answer key revealed.
 *
 * Advanced types render through the REAL exam players in `disabled` mode, seeded
 * with the correct answer, so an author sees exactly the layout a candidate
 * will — and math, code, formatting and images come for free via the players'
 * own `RenderedHtml` usage rather than being reimplemented here.
 *
 * Used by the question-bank preview drawer, the two-pane editor's live preview,
 * and the generated-questions review dialog. Before this existed each of those
 * hardcoded its own choice-type markup and rendered nothing at all for the other
 * eight types.
 */

/** No-op: previews are inert, but the players require a change handler. */
const noop = () => undefined;

export interface QuestionPreviewData {
  type: string;
  prompt: string;
  options?: { id: string; value: string }[];
  matchPairs?: { key: string; value: string }[];
  correctAnswer?: string | string[] | Record<string, string> | null;
  explanation?: string;

  // Type-specific
  rubric?: string;
  answerMode?: 'exact' | 'manual';
  acceptedAnswers?: string[];
  maxWords?: number | null;
  matchingOptions?: string[];
  blanks?: BlankSlot[];
  reorderItems?: ReorderItem[];
  correctOrder?: string[];
  passageText?: string;
  passageTitle?: string;
  subQuestions?: unknown[];
  pages?: PassagePage[];
}

export interface QuestionPreviewProps {
  question: QuestionPreviewData;
  /** `compact` tightens vertical rhythm for dense lists; `comfortable` is the default. */
  density?: 'comfortable' | 'compact';
  /** Renders the explanation block when the question has one. */
  showExplanation?: boolean;
  className?: string;
}

/** True when an option is (one of) the correct answer(s), by id or by value. */
function isOptionCorrect(
  optionId: string,
  optionValue: string,
  correctAnswer: QuestionPreviewData['correctAnswer'],
): boolean {
  if (correctAnswer == null) return false;
  const answers = Array.isArray(correctAnswer)
    ? correctAnswer
    : typeof correctAnswer === 'object'
      ? Object.values(correctAnswer)
      : [correctAnswer];
  return answers.includes(optionId) || answers.includes(optionValue);
}

export const QuestionPreview: React.FC<QuestionPreviewProps> = ({
  question,
  density = 'comfortable',
  showExplanation = false,
  className,
}) => {
  const { t } = useTranslation();
  const compact = density === 'compact';
  const gap = compact ? 'gap-2' : 'gap-3.5';
  const optionGap = compact ? 'gap-1' : 'gap-1.5';
  const optionPad = compact ? 'px-2.5 py-1.5' : 'px-3 py-2.5';

  /** Stem, unless the type's player owns the prompt text itself. */
  const promptOwnedByPlayer =
    question.type === 'FILL_IN_BLANKS' ||
    question.type === 'PASSAGE';

  const renderBody = () => {
    switch (question.type) {
      case 'MULTIPLE_CHOICE':
      case 'MULTIPLE_RESPONSE':
      case 'TRUE_FALSE': {
        const options = question.options ?? [];
        if (!options.length) return null;
        return (
          <div className={cn('flex flex-col', optionGap)}>
            {options.map((opt, index) => {
              const correct = isOptionCorrect(opt.id, opt.value, question.correctAnswer);
              const multi = question.type === 'MULTIPLE_RESPONSE';
              return (
                <div
                  key={opt.id}
                  className={cn(
                    'flex items-center gap-2.5 rounded-md border text-[13px]',
                    optionPad,
                    correct
                      ? 'border-success-600 bg-success-50 font-medium text-foreground dark:bg-success-900/20'
                      : 'border-border text-foreground/80',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center',
                      multi ? 'rounded-[4px]' : 'rounded-full',
                      correct
                        ? 'bg-success-600 text-white'
                        : 'border border-input',
                    )}
                  >
                    {correct && (multi ? <Check className="h-3 w-3" strokeWidth={3} /> : <span className="h-1.5 w-1.5 rounded-full bg-white" />)}
                  </span>
                  {htmlIsEmpty(opt.value) ? (
                    <span className="text-muted-foreground italic">
                      {t('option_index', { index: index + 1 })}
                    </span>
                  ) : (
                    <RenderedHtml html={getQuestionOptionLabel(question.type, opt.value, t)} inline />
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      case 'SHORT_ANSWER': {
        const expected =
          question.acceptedAnswers?.filter(Boolean).join(' / ') || (typeof question.correctAnswer === 'string' ? question.correctAnswer : '');
        return (
          <div className="inline-block rounded-md bg-muted/60 px-3 py-1.5">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {t('expected')}{' '}
            </span>
            <span className="text-[13px] text-foreground">
              {expected || <span className="italic text-muted-foreground">{t('no_answer_key')}</span>}
            </span>
          </div>
        );
      }

      case 'MATCHING': {
        const pairs = question.matchPairs ?? [];
        if (!pairs.length) return null;
        return (
          <div className={cn('flex flex-col', optionGap)}>
            {pairs.map((pair, index) => (
              <div key={index} className="flex flex-wrap items-center gap-2 text-[13px]">
                <span className="rounded-md bg-muted/60 px-2.5 py-1">
                  <RenderedHtml html={pair.key || ''} inline />
                </span>
                <span className="text-muted-foreground">→</span>
                <span className="rounded-md bg-success-50 px-2.5 py-1 text-success-700 dark:bg-success-900/20 dark:text-success-300">
                  <RenderedHtml html={pair.value || ''} inline />
                </span>
              </div>
            ))}
          </div>
        );
      }

      case 'FILL_IN_BLANKS':
        return (
          <FillInBlanksPlayer
            prompt={question.prompt}
            blanks={question.blanks ?? []}
            value={correctBlanksValue(question.blanks)}
            onChange={noop}
            disabled
          />
        );

      case 'REORDER':
        return (
          <ReorderPlayer
            items={question.reorderItems ?? []}
            value={correctReorderValue(question.reorderItems, question.correctOrder)}
            onChange={noop}
            disabled
          />
        );

      case 'PASSAGE':
        return (
          <PassagePlayer
            passageText={question.passageText ?? question.prompt}
            passageTitle={question.passageTitle}
            subQuestions={(question.subQuestions ?? []) as never}
            pages={question.pages}
            value={correctPassageValue(question.subQuestions)}
            onChange={noop}
            disabled
          />
        );

      case 'OPEN_ENDED':
        return (
          <OpenEndedPlayer
            value=""
            onChange={noop}
            rubric={question.rubric}
            disabled
            placeholder={t('open_ended_preview_placeholder')}
          />
        );

      default:
        // Never return null here: a type this renderer doesn't know about must
        // be visible, not silently blank.
        return (
          <div className="flex items-center gap-2 rounded-md border border-warning-300 bg-warning-50 px-3 py-2 text-[13px] text-warning-800 dark:border-warning-900 dark:bg-warning-900/20 dark:text-warning-200">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
            {t('preview_unsupported_type', { type: question.type })}
          </div>
        );
    }
  };

  return (
    <div className={cn('flex flex-col', gap, className)}>
      {!promptOwnedByPlayer &&
        (htmlIsEmpty(question.prompt) ? (
          <p
            className={cn(
              'font-medium italic leading-relaxed text-muted-foreground',
              compact ? 'text-sm' : 'text-[15px]',
            )}
          >
            {t('editor_preview_empty_prompt')}
          </p>
        ) : (
          <RenderedHtml
            html={question.prompt}
            className={cn(
              'font-medium leading-relaxed text-foreground',
              compact ? 'text-sm' : 'text-[15px]',
            )}
          />
        ))}

      {renderBody()}

      {showExplanation && question.explanation && !htmlIsEmpty(question.explanation) && (
        <div className="border-t border-border pt-2.5">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t('explanation')}
          </p>
          <RenderedHtml
            html={question.explanation}
            className="text-xs leading-relaxed text-foreground/80"
          />
        </div>
      )}
    </div>
  );
};

export default QuestionPreview;
